import type { ReactNode } from "react";

/**
 * The arrow a `<select>` in this application draws for itself.
 *
 * ==========================================================================================
 * IT IS DRAWN RATHER THAN INHERITED, AND THAT IS A PLATFORM CONSTRAINT
 * ==========================================================================================
 *
 * A native `<select>`'s arrow is painted by the platform against the BORDER BOX, and `padding-right`
 * moves only the text — so the arrow cannot be inset, and a control with room on the right still has
 * its glyph jammed against the edge. The only way to place it is to drop `appearance` and draw one.
 *
 * `pointer-events-none` so the click still reaches the select underneath, and `aria-hidden` because
 * the select already carries its own accessible name: naming the arrow as well would make a screen
 * reader announce the control's purpose twice, once vaguely.
 *
 * INLINE SVG rather than an icon package. `@gladis/ui` depends on no icon set — `Dialog`'s close
 * button and `Button`'s spinner make the same call. The path is the shape `@heroicons`'
 * `ChevronDownIcon` draws, which is what `apps/app` used before `FilterSelect` existed, so nothing
 * changed on screen when the dashboard moved onto it.
 *
 * ==========================================================================================
 * IT IS ITS OWN FILE BECAUSE THERE ARE TWO SELECTS, NOT ONE
 * ==========================================================================================
 *
 * `FilterSelect` says what you are LOOKING AT; `SelectField` COLLECTS something. They differ in
 * fill and border and in nothing else, so the geometry — the arrow's size, its inset, and the
 * `pr-9` that makes room for it — has to be the same in both or the two sit differently on a page
 * that shows them together. One component, imported twice.
 */
export function SelectChevron(): ReactNode {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="pointer-events-none absolute right-3 size-3.5 text-muted"
    >
      <path d="M5 7.5l5 5 5-5" />
    </svg>
  );
}

/**
 * What a select needs on top of a field's or a filter's own appearance, in one place.
 *
 * `appearance-none` removes the platform arrow, `pr-9` makes room for the drawn one, and
 * `[&>option]:bg-page` gives the dropped-down menu a surface — a translucent select opens a list the
 * platform paints against the page's default white, and on the dark theme the options are
 * unreadable until they are given one.
 *
 * THE `pr-9` LAYERS OVER THE BASE'S `px-3`, which `cn` joins rather than merges. `FilterSelect` has
 * composed these two exactly this way since it shipped and renders correctly, so the emitted order
 * is settled by precedent rather than by hope — but it IS the trap `lib/cn.ts` documents, and a
 * third caller should reach for a padding-free base instead of a fourth layer.
 */
export const SELECT_CHROME = "cursor-pointer appearance-none pr-9 [&>option]:bg-page";
